import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, Box } from "@mui/material";
import { useEffect, useState, useRef, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { GenericImageSelector, ProcessedImage } from "@/components/general/Images/GenericImageSelector";
import ComercialDrawer from "../../../../../components/pet-beauty/CormercialDrawer";
import { PrimaryButton } from "../../../../../components/general/buttons/PrimaryButton";
import { SecondaryButton } from "../../../../../components/general/buttons/SecondaryButton";
import { LeftMarginBackButton } from "@/components/general/buttons/BackPageButton";
import serviceCategoryApi from "../../../../../services/api/ServiceCategory";
import serviceStoreApi from "../../../../../services/api/ServiceStore";
import { IServiceCategory } from "../../../../../services/api/ServiceCategory/types";
import { IStore } from "../../../../../services/api/Store/types";
import { addServiceSchema } from "../../../../../utils/yupSchemas";
import { sharedStyles } from "../../../../../styles/shared";
import { getLocalStorageItem } from "../../../../../hooks/localStorageHooks";
import useFetchEmployeesFromStore from "../../../../../hooks/entities/useFetchEmployeesFromStore";
import useFetchSearchServices from "../../../../../hooks/entities/useFetchSearchServices";
import { createServiceEmployee, validateServiceStorePrices } from "../usualFunctions";
import {EmployeeSelectionForm} from "@/pages/petAndBeauty/Comercial/ServicesPages/ServiceForm/EmployeeSelectionForm";
import {ServiceDataForm} from "@/pages/petAndBeauty/Comercial/ServicesPages/ServiceForm/ServiceDataForm";
import {DynamicPriceForm} from "@/pages/petAndBeauty/Comercial/ServicesPages/ServiceForm/DynamicPriceForm";

const AddService = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const selectedStore: IStore = getLocalStorageItem("selectedStore");

  const [selectedImages, setSelectedImages] = useState<ProcessedImage[]>([]);

  const handleImageChange = useCallback((images: ProcessedImage[]) => {
    setSelectedImages(images);
  }, []);

  const refs = {
    serviceName: useRef<HTMLInputElement>(null),
    description: useRef<HTMLInputElement>(null),
  };

  const [serviceCategory, setServiceCategory] = useState<string>("");
  const [durationOfService, setDurationOfService] = useState<number>(1);
  const [dynamicPriceAllowed, setIsDynamicPriceAllowed] = useState<boolean>(false);
  const [fixedPrice, setFixedPrice] = useState<number | null>(null);

  const [dynamicPrices, setDynamicPrices] = useState<(number | null)[]>([
    null, null, null, null, null, null, null
  ]);

  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
  const [buttonsDisabled, setButtonsDisabled] = useState<boolean>(false);
  const [submittedOnce, setSubmittedOnce] = useState<boolean>(false);
  const [generalErrorMessage, setGeneralErrorMessage] = useState<string>("");

  const { services } = useFetchSearchServices(serviceCategory);
  const { employees } = useFetchEmployeesFromStore(selectedStore?.id);

  const noEmployeesSelected = selectedEmployeeIds.length === 0;

  const checkIfPricesAreValid = () =>
    validateServiceStorePrices(
      fixedPrice,
          dynamicPrices[0],
          dynamicPrices[1],
          dynamicPrices[2],
          dynamicPrices[3],
          dynamicPrices[4],
          dynamicPrices[5],
          dynamicPrices[6],
      dynamicPriceAllowed
    );

  const {
    register,
    handleSubmit: onSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(addServiceSchema) });

  useEffect(() => {
    setGeneralErrorMessage("");
  }, [serviceCategory]);

  // Aplicação do Novo Padrão de Envio via FormData unificado (Sem Firebase)[cite: 3]
  const handleServiceAndServiceStoreCreation = async () => {
    setGeneralErrorMessage("");
    if (!submittedOnce) setSubmittedOnce(true);

    const pricesAreValid = checkIfPricesAreValid();

    if (serviceCategory.length > 0 && pricesAreValid && !noEmployeesSelected) {
      setButtonsDisabled(true);

      try {
        let serviceCategoryId = "";

        // 1. Cria ou recupera a categoria de serviço se não existir
        if (services.length === 0) {
          const createdCategory = await serviceCategoryApi.createService({
            name: serviceCategory,
          } as IServiceCategory);
          serviceCategoryId = createdCategory.id;
        } else {
          serviceCategoryId = services[0].id;
        }

        const maximumWeekPrice = Math.max(...dynamicPrices.map((p) => p || 0));
        const serviceName = refs.serviceName.current?.value || "";

        const serviceStoreData = {
          serviceName,
          serviceId: serviceCategoryId,
          storeId: selectedStore.id,
          description: refs.description?.current?.value || "",
          price: !dynamicPriceAllowed ? fixedPrice : maximumWeekPrice,
          priceDay: dynamicPriceAllowed ? dynamicPrices : Array(7).fill(fixedPrice),
          durationOfService,
        };

        const formData = new FormData();
        formData.append("serviceStore", JSON.stringify(serviceStoreData));

        selectedImages.forEach((img, index) => {
          formData.append("images", img.resizedFull, `service_image_${index}.jpg`);
        });

        const createdServiceStoreResponse = await serviceStoreApi.createServiceStore(formData);

        for (const employeeId of selectedEmployeeIds) {
          await createServiceEmployee(employeeId, createdServiceStoreResponse.id);
        }

        window.alert(t("servicePage.management.add.success"));
        navigate("/services");
      } catch (error: any) {
        setButtonsDisabled(false);
        console.error("Error creating a service or services store: ", error);
        setGeneralErrorMessage(error.response?.data?.message || t("servicePage.management.add.unknownError"));
      }
    }
  };

  const renderAddServicePageContent = (
    <>
      <LeftMarginBackButton />

        <form onSubmit={onSubmit(handleServiceAndServiceStoreCreation)} style={sharedStyles.form}>
          <ServiceDataForm
              register={register}
              errors={errors}
              refs={refs}
              services={services}
              serviceCategory={serviceCategory}
              setServiceCategory={setServiceCategory}
              durationOfService={durationOfService}
              setDurationOfService={setDurationOfService}
              fixedPrice={fixedPrice}
              setFixedPrice={setFixedPrice}
              dynamicPriceAllowed={dynamicPriceAllowed}
              setIsDynamicPriceAllowed={setIsDynamicPriceAllowed}
              pricesAreValid={checkIfPricesAreValid()}
              submittedOnce={submittedOnce}
          />

          <DynamicPriceForm
              dynamicPriceAllowed={dynamicPriceAllowed}
              pricesAreValid={checkIfPricesAreValid()}
              submittedOnce={submittedOnce}
              dynamicPrices={dynamicPrices}
              setDynamicPrices={setDynamicPrices}
          />

          <GenericImageSelector
              fullWidth={800}
          fullHeight={600}
          dropzoneText="Selecione ou arraste as fotos do serviço"
          onChangeImages={handleImageChange}
          />

          <EmployeeSelectionForm
              employees={employees}
              selectedEmployeeIds={selectedEmployeeIds}
              setSelectedEmployeeIds={setSelectedEmployeeIds}
              noEmployeesSelected={noEmployeesSelected && submittedOnce}
          />

        {generalErrorMessage !== "" && (
          <Alert severity="error">{generalErrorMessage}</Alert>
        )}

        <Box sx={sharedStyles.dividedFormContainer}>
          <PrimaryButton
            label={t("servicePage.management.add.confirm")}
            isLoading={buttonsDisabled}
            icon={<AddIcon />}
            onClickAction={() => !submittedOnce && setSubmittedOnce(true)}
          />

          <SecondaryButton
            label={t("servicePage.management.add.cancel")}
            onClickAction={() => navigate("/services")}
            isDisabled={buttonsDisabled}
          />
        </Box>
      </form>
    </>
  );

  return (
    <Box sx={sharedStyles.sharedMainContainer}>
      <ComercialDrawer content={renderAddServicePageContent} />
    </Box>
  );
};

export default AddService;
